import { Button, type ButtonProps, LoadingSpinner } from "@/components/ui";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/shadcn/DropdownMenu/DropdownMenu";
import { ChevronDown, type LucideIcon } from "lucide-react";
import { useFormContext } from "react-hook-form";

export interface FormSubmitButtonAction {
  label: string;
  onSelect: () => void;
  disabled?: boolean;
  icon?: LucideIcon;
}

export const FormSubmitButton = ({
  loading,
  title,
  variant,
  disabled,
  submitFn,
  overrideDisabled,
  type,
  submitFormId,
  additionalActions,
}: {
  variant?: ButtonProps["variant"];
  loading?: boolean;
  title?: string;
  disabled?: boolean;
  submitFn?: () => void;
  overrideDisabled?: boolean;
  type?: ButtonProps["type"];
  /** Associate submit with a `<form id="...">` when the button sits outside that form. */
  submitFormId?: string;
  /** Optional actions displayed from a split-button arrow beside the submit button. */
  additionalActions?: FormSubmitButtonAction[];
}) => {
  const form = useFormContext();
  const isDisabled =
    disabled ||
    !!loading ||
    (!overrideDisabled && Object.keys(form.formState.dirtyFields).length === 0);
  const submitButton = (
    <Button
      variant={variant ?? "default"}
      type={type ?? "submit"}
      form={submitFormId}
      disabled={isDisabled}
      data-testid="form-submit-button"
      onClick={() => {
        submitFn?.();
      }}
      className={additionalActions?.length ? "rounded-r-none" : undefined}
    >
      {loading ? <LoadingSpinner /> : (title ?? "Save")}
    </Button>
  );

  if (!additionalActions?.length) {
    return submitButton;
  }

  return (
    <div className="flex items-center">
      {submitButton}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant={variant ?? "default"}
            className="rounded-l-none border-l border-l-current/20 px-2"
            disabled={isDisabled}
            aria-label="Additional save actions"
          >
            {loading ? <LoadingSpinner /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {additionalActions.map((action) => {
            const Icon = action.icon;
            return (
              <DropdownMenuItem
                key={action.label}
                disabled={action.disabled}
                onSelect={action.onSelect}
              >
                {Icon ? <Icon className="mr-2 h-4 w-4" /> : null}
                {action.label}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
